import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { PageHeader, Panel } from "@/components/PageHeader";
import { MovementsToolbar, type Filters } from "@/components/movements/MovementsToolbar";
import { MovementsList } from "@/components/movements/MovementsList";
import { TransactionFormDialog } from "@/components/movements/TransactionFormDialog";
import { DeleteTransactionDialog } from "@/components/movements/DeleteTransactionDialog";
import { api, formatApiError } from "@/lib/api";
import { useDebounced } from "@/lib/hooks";
import type { Account, AccountsResponse, Category, Transaction, TxListResponse } from "@/lib/finance";
import type { BudgetItemRow } from "@/lib/budget";
import { currentMonth, money, monthName } from "@/lib/format";

export default function MovementsPage() {
  const [params] = useSearchParams();
  const [filters, setFilters] = useState<Filters>({ month: params.get("month") ?? currentMonth(), type: (params.get("type") as Filters["type"]) ?? "all", accountId: "all", categoryId: "all", q: "" });
  const q = useDebounced(filters.q);
  const [data, setData] = useState<TxListResponse | null>(null);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [items, setItems] = useState<BudgetItemRow[]>([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Transaction | null>(null);
  const [deleting, setDeleting] = useState<Transaction | null>(null);
  const [downloading, setDownloading] = useState(false);

  const load = useCallback(() => {
    const params: Record<string, string> = { month: filters.month };
    if (filters.type !== "all") params.type = filters.type;
    if (filters.accountId !== "all") params.account_id = filters.accountId;
    if (filters.categoryId !== "all") params.category_id = filters.categoryId;
    if (q.trim()) params.q = q.trim();
    api.get<TxListResponse>("/transactions", { params }).then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, [filters.month, filters.type, filters.accountId, filters.categoryId, q]);

  useEffect(load, [load]);
  useEffect(() => {
    api.get<AccountsResponse>("/accounts", { params: { include_archived: true } }).then((r) => setAccounts(r.data.accounts));
    api.get<Category[]>("/categories").then((r) => setCategories(r.data));
    api.get<BudgetItemRow[]>("/budget-items").then((r) => setItems(r.data));
  }, []);

  const confirmDelete = async () => {
    if (!deleting) return;
    try {
      await api.delete(`/transactions/${deleting.id}`);
      toast.success("Movimiento borrado");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setDeleting(null);
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const r = await api.get("/transactions/month-pdf", { params: { month: filters.month }, responseType: "blob" });
      const url = URL.createObjectURL(r.data as Blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `movimientos-${filters.month}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("No se pudo generar el PDF"); }
    setDownloading(false);
  };

  const s = data?.summary;
  return (
    <section data-testid="movements-page">
      <PageHeader title="Movimientos" testId="movements-title" actions={
        <Button onClick={() => { setEditing(null); setFormOpen(true); }} className="h-11 bg-[#1B2A4A] text-white hover:bg-[#2A3D63]" data-testid="new-transaction-button">
          <Plus className="mr-2 h-4 w-4" /> Nuevo movimiento
        </Button>
      } />
      <Panel className="fade-up">
        <MovementsToolbar filters={filters} onChange={(p) => setFilters((f) => ({ ...f, ...p }))} accounts={accounts}
          categories={categories} onDownload={downloadPdf} downloading={downloading} />
        {!data ? <div className="mt-6 h-64 animate-pulse rounded-lg bg-[#F4F5F7]" /> : (
          <MovementsList items={data.items} onEdit={(t) => { setEditing(t); setFormOpen(true); }} onDelete={setDeleting} />
        )}
        {s && (
          <div className="mt-4 flex flex-col gap-2 border-t border-[#DDE1E8] pt-4 text-sm sm:flex-row sm:items-center sm:justify-between" data-testid="movements-summary">
            <span className="text-slate-500" data-testid="summary-count">{s.count} movimientos en {monthName(filters.month)}</span>
            <div className="tabular flex flex-wrap gap-x-6 gap-y-1">
              <span>Entró <b className="text-[#1B6E4F]" data-testid="summary-income">{money(s.income_total)}</b></span>
              <span>Salió <b className="text-[#B23A1E]" data-testid="summary-expense">{money(s.expense_total)}</b></span>
              <span>Balance <b className={s.balance < 0 ? "text-[#B23A1E]" : "text-[#1B2A4A]"} data-testid="summary-balance">{money(s.balance)}</b></span>
            </div>
          </div>
        )}
      </Panel>
      <p className="mt-3 text-xs text-slate-400">El balance excluye traspasos y ajustes.</p>
      <TransactionFormDialog open={formOpen} onOpenChange={setFormOpen} accounts={accounts} categories={categories} items={items} initial={editing} onSaved={load} />
      <DeleteTransactionDialog tx={deleting} onCancel={() => setDeleting(null)} onConfirm={confirmDelete} />
    </section>
  );
}
